import React from "react";
import { Button, Paper, Stack, Typography } from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import { t } from "i18next";
import { FtlStreakResults as FtlStreakResultsType, Player } from "../../shared/interfaces/GameLogicTypes";
import useSocketContext from "../../shared/useSocketContext";
import "./FtlStreakResults.css";

export interface FtlStreakResultsProps {
  streakResults: FtlStreakResultsType;
  players: { [playerId: string]: Player };
  isMobile?: boolean;
  waitForHost: boolean;
  isHost: boolean;
  isReady: boolean;
  readyPlayerIds: string[];
  totalPlayers: number;
  onReadyToggle: (next: boolean) => void;
}

interface PlayerRow {
  playerId: string;
  name: string;
  totalPoints: number;
  survivedAll: boolean;
  eliminatedOnTurn: number; // -1 if survived all
  turnResults: { survived: boolean; points: number; color?: string }[];
}

export default function FtlStreakResultsPanel({
  streakResults,
  players,
  isMobile,
  waitForHost,
  isHost,
  isReady,
  readyPlayerIds,
  totalPlayers,
  onReadyToggle,
}: FtlStreakResultsProps) {
  const socket = useSocketContext().socket;

  // Build player rows (non-czar participants)
  const allPlayerIds = new Set<string>();
  streakResults.turnResults.forEach((tr) => {
    tr.survivors.forEach((id) => allPlayerIds.add(id));
    tr.eliminations.forEach((id) => allPlayerIds.add(id));
  });

  const rows: PlayerRow[] = Array.from(allPlayerIds).map((pid) => {
    const turnResults: PlayerRow["turnResults"] = [];
    let eliminatedOnTurn = -1;
    let survivedAll = true;

    streakResults.turnResults.forEach((tr, i) => {
      const wasEliminated = tr.eliminations.includes(pid);
      const wasSurvivor = tr.survivors.includes(pid);
      const points = tr.pointsAwarded[pid] ?? 0;

      if (wasEliminated) {
        eliminatedOnTurn = i;
        survivedAll = false;
      }

      if (wasSurvivor || wasEliminated) {
        turnResults.push({ survived: !wasEliminated, points });
      }
    });

    return {
      playerId: pid,
      name: players[pid]?.name ?? pid,
      totalPoints: streakResults.totalPointsByPlayer[pid] ?? 0,
      survivedAll,
      eliminatedOnTurn,
      turnResults,
    };
  });

  const czarId = streakResults.czarId;
  const czarTotalPoints = streakResults.totalPointsByPlayer[czarId] ?? 0;

  // Sort: survivors first (by points desc), then eliminated (by turn survived desc)
  rows.sort((a, b) => {
    if (a.survivedAll && !b.survivedAll) return -1;
    if (!a.survivedAll && b.survivedAll) return 1;
    if (a.survivedAll && b.survivedAll) return b.totalPoints - a.totalPoints;
    return b.eliminatedOnTurn - a.eliminatedOnTurn;
  });

  const maxPoints = Math.max(...rows.map((r) => r.totalPoints), 1);

  return (
    <Paper
      elevation={3}
      sx={{
        width: "100%",
        p: 2,
      }}
    >
      <Stack spacing={2}>
        <Typography variant="h5" fontWeight="bold" textAlign="center">
          {t("ftl_streak_results")}
        </Typography>

        {/* Czar label with points */}
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {t("ftl_leader")} {players[czarId]?.name ?? czarId} — {czarTotalPoints} pts
        </Typography>

        {/* Bar chart */}
        <div className="ftl-streak-results">
          {rows.map((row) => (
            <div key={row.playerId} className="ftl-result-row">
              <div className="ftl-result-name">{row.name}</div>
              <div className="ftl-result-bar">
                {row.turnResults.map((tr, i) => {
                  const widthPct = Math.max(
                    15,
                    (tr.points / maxPoints) * 100
                  );
                  return (
                    <div
                      key={i}
                      className={`ftl-result-segment ${tr.survived ? "survived" : "eliminated"}`}
                      style={{
                        width: `${widthPct}%`,
                        backgroundColor: tr.survived
                          ? "rgba(146, 220, 103, 0.7)"
                          : "rgba(231, 81, 81, 0.7)",
                        animationDelay: `${i * 0.3}s`,
                      }}
                    >
                      {tr.points > 0 ? `+${tr.points}` : ""}
                    </div>
                  );
                })}
              </div>
              <div className="ftl-result-end-marker">
                {row.survivedAll ? (
                  <FlagIcon sx={{ fontSize: 20, color: "#92dc67" }} />
                ) : (
                  <span role="img" aria-label="skull">&#128128;</span>
                )}
              </div>
              <div className="ftl-result-points">{row.totalPoints} pts</div>
            </div>
          ))}
        </div>

        {/* Ready/Continue button */}
        <Stack alignItems="center">
          {waitForHost ? (
            isHost ? (
              <Button variant="contained" onClick={() => socket.emit("nextTurn")}>
                {t("continue")}
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("waiting_for_host")}
              </Typography>
            )
          ) : (
            <Button
              variant={isReady ? "outlined" : "contained"}
              onClick={() => onReadyToggle(!isReady)}
            >
              {isReady ? t("unready") : t("ready")} ({readyPlayerIds.length}/{totalPlayers})
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}
